import ChevronIcon from "@/assets/colorable/chevron.svg?react";
import TickIcon from "@/assets/colorable/tick.svg?react";
import ReusableDropdown, {type TDropdownOption} from "@/components/Common/forms/ReusableDropdown";
import Loader from "@/components/Common/kpi/Loader";
import clsxm from "@/lib/clsxm";
import type {TCurrency} from "@/store/types";
import type {FC} from "react";
import {useTranslation} from "react-i18next";

const CURRENCIES: {value: TCurrency; label: string}[] = [
  {
    value: "EUR",
    label: "EUR",
  },
  {
    value: "CHF",
    label: "CHF",
  },
  {
    value: "GBP",
    label: "GBP",
  },
  {
    value: "USD",
    label: "USD",
  },
];

const CurrencySelector: FC<{
  buttonClassName?: string;
  className?: string;
  isPending?: boolean;
  selectedCurrency: TCurrency;
  setCurrency(option: TDropdownOption): void;
}> = ({buttonClassName, className, isPending = false, selectedCurrency = "CHF", setCurrency}) => {
  const {t} = useTranslation("common");
  const currency = CURRENCIES.find(c => c.value === selectedCurrency);

  return (
    <ReusableDropdown
      containerClassName={clsxm("w-min", className)}
      setSelectedOption={setCurrency}
      selectedOption={currency}
      options={CURRENCIES}
      renderButton={({selectedOption, isOpen, className, disabled, ...rest}) => (
        <button
          {...rest}
          disabled={isPending || disabled}
          className={clsxm(
            className,
            isOpen ? "border-primary-medium-solid" : "border-neutral-grey-light",
            selectedOption ? "text-neutral-black font-medium" : "text-neutral-grey-light",
            "gap-2",
            isPending ? "cursor-wait items-center justify-center" : "pl-4",
            buttonClassName,
          )}>
          {isPending ? (
            <Loader />
          ) : (
            <>
              <span className="grow items-center text-left text-base">
                {selectedOption?.label ?? t("selectCurrency")}
              </span>
              <ChevronIcon
                className={clsxm(
                  "text-primary-dark-solid mr-4 transition-transform duration-250",
                  isOpen ? "rotate-180" : "rotate-0",
                )}
              />
            </>
          )}
        </button>
      )}
      renderItem={({option, isHighlighted, isSelected, className, ...rest}) => {
        return (
          <li
            {...rest}
            value={option.value}
            className={clsxm(
              className,
              isHighlighted ? "bg-primary-light-solid" : "hover:bg-neutral-white-dark",
              "gap-2",
            )}>
            <span className="grow leading-none">{option.label}</span>
            {isSelected && <TickIcon className="text-primary-dark-solid" />}
          </li>
        );
      }}
    />
  );
};

export default CurrencySelector;
